import { CloseOutlined, FileProtectOutlined, LogoutOutlined, SwapOutlined, UserOutlined, WarningOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { Dropdown, Flex, Tag, Typography, theme } from 'antd';
import type { ItemType } from 'antd/es/menu/interface';
import type React from 'react';
import type { ReactNode } from 'react';

const { useToken } = theme;
const { Text } = Typography;

interface UserMenuProps {
  roleName?: string;
  username?: string;
  avatar: ReactNode;
  doctorName: string;
  userFullName: string;
  disabled: boolean;
  showDoctorSelect: boolean;
  showDoctorReset: boolean;
  onDoctorSelect: () => void;
  onDoctorReset: () => void;
  onAccountOpen?: () => void;
  onLogout: () => void;
}

export const UserMenu: React.FC<UserMenuProps> = ({
  roleName,
  username,
  avatar,
  doctorName,
  disabled,
  userFullName,
  showDoctorSelect,
  showDoctorReset,
  onDoctorSelect,
  onDoctorReset,
  onAccountOpen,
  onLogout,
}) => {
  const { token } = useToken();
  const styles = {
    role: css`
      font-size: ${token.fontSizeSM}px;
    `,
    bold: css`
      font-weight: 500;
    `,
    innerWrapper: css`
      display: flex;
      gap: ${token.paddingXS}px;
      align-items: center;
      cursor: pointer;
    `,
    name: css`
      text-align: right;
      display: inline-block;
      max-width: 200px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-size: ${token.fontSizeSM}px;
    `,
  };

  let items: ItemType[] = [
    {
      key: 'info',
      disabled: true,
      style: { cursor: 'default', whiteSpace: 'normal' },
      label: (
        <Flex vertical>
          <Text type="secondary" className={css(`margin-bottom: ${token.marginXS}px`)}>
            Eingeloggt als
          </Text>
          <Text className={styles.bold}>{userFullName}</Text>
          <Text className={styles.role} type="secondary">
            {roleName}
          </Text>
        </Flex>
      ),
    },
    { type: 'divider' },
  ];

  if (showDoctorSelect) {
    items = [
      ...items,
      {
        key: 'selectDoctor',
        icon: <SwapOutlined />,
        onClick: onDoctorSelect,
        label: 'Zuweiser festlegen',
      },
    ];
  }

  if (showDoctorReset) {
    items = [
      ...items,
      {
        key: 'resetDoctor',
        icon: <CloseOutlined />,
        onClick: onDoctorReset,
        label: 'Zuweiser zurücksetzen',
      },
    ];
  }

  if (showDoctorSelect || showDoctorReset) {
    items = [...items, { type: 'divider' }];
  }

  items = [
    ...items,
    {
      key: 'app-switcher',
      icon: <FileProtectOutlined />,
      onClick: () => (window.location.href = window._env_.BEFUNDE_URL),
      label: 'Befunde',
      className: css`
        @media (min-width: ${token.screenMD}px) {
          display: none !important;
        }
      `,
    },
  ];

  if (onAccountOpen) {
    items = [
      ...items,
      {
        key: 'account',
        icon: <UserOutlined />,
        onClick: onAccountOpen,
        label: 'Benutzerkonto',
      },
    ];
  }

  items = [
    ...items,
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      danger: true,
      onClick: onLogout,
      label: 'Abmelden',
    },
  ];

  return (
    <Dropdown
      menu={{ items: items }}
      trigger={['click']}
      arrow
      placement="bottomRight"
      styles={{ root: { minWidth: 250, width: 250, maxWidth: 250 } }}
    >
      <div className={styles.innerWrapper}>
        {avatar}
        <div className={styles.name}>
          <span>{username}</span>
          <br />
          <span>
            {disabled && (
              <Tag color="warning" icon={<WarningOutlined />}>
                Deaktiviert
              </Tag>
            )}{' '}
            {doctorName}
          </span>
        </div>
      </div>
    </Dropdown>
  );
};
